import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { User } from '@/modules/user/models/User';
import { ContentSourceDocument } from './ContentSourceDocument';
import { ContentSourceJurisdiction } from '@/modules/ai/models/ContentSourceJurisdiction';
import { Country } from '@/modules/predefined/models/Country';
import { County } from '@/modules/predefined/models/County';
import { ContentSourceStatus } from '@/modules/ai/constants/ContentSourceStatus';
import { ContentSourceType } from '@/modules/ai/constants/ContentSourceType';
import { markdownToHtml } from '@/modules/core/app/helpers/StringHelper';
import { ContentSourceRole } from '@/modules/ai/models/ContentSourceRole';

export class ContentSource extends BaseEntityModel {
  id?: number;
  ragId?: string;
  title?: string;
  content?: string;
  status?: string;
  type?: string;
  url?: string;
  estateType?: string;
  planTypes?: string[];
  countryId?: number;
  countyId?: number;
  sourceLastUpdatedAt?: string;
  lastEditedUserId?: number;

  lastEditedUser: Relationship<User | null>;
  contentSourceDocument: Relationship<ContentSourceDocument | null>;
  contentSourceJurisdictions: Relationship<ContentSourceJurisdiction[] | null>;
  country: Relationship<Country | null>;
  county: Relationship<County | null>;
  contentSourceRoles: Relationship<ContentSourceRole[] | null>;

  constructor(model: Partial<ContentSource> = {}) {
    super(model);

    this.id = model.id;
    this.ragId = model.ragId;
    this.title = model.title;
    this.content = model.content;
    this.status = model.status;
    this.type = model.type;
    this.url = model.url;
    this.estateType = model.estateType;
    this.planTypes = model.planTypes ?? [];
    this.countryId = model.countryId;
    this.countyId = model.countyId;
    this.sourceLastUpdatedAt = model.sourceLastUpdatedAt;
    this.lastEditedUserId = model.lastEditedUserId;

    this.lastEditedUser = User.mapModelOrNull(model.lastEditedUser);
    this.contentSourceDocument = ContentSourceDocument.mapModelOrNull(model.contentSourceDocument);
    this.contentSourceJurisdictions = ContentSourceJurisdiction.mapModelsOrNull(
      model.contentSourceJurisdictions
    );
    this.country = Country.mapModelOrNull(model.country);
    this.county = County.mapModelOrNull(model.county);
    this.contentSourceRoles = ContentSourceRole.mapModelsOrNull(model.contentSourceRoles);
  }

  get isDraft(): boolean {
    return this.status === ContentSourceStatus.DRAFT.key;
  }

  get isPublished(): boolean {
    return this.status === ContentSourceStatus.PUBLISHED.key;
  }

  get isTextType(): boolean {
    return this.type === ContentSourceType.TEXT.key;
  }

  get isPdfType(): boolean {
    return this.type === ContentSourceType.PDF.key;
  }

  get isProcessing(): boolean {
    return [ContentSourceStatus.SCANNING.key, ContentSourceStatus.EMBEDDING.key].includes(
      this.status ?? ''
    );
  }

  get isEstateLawType(): boolean {
    return this.type === ContentSourceType.ESTATE_LAW.key;
  }

  get isPlannerType(): boolean {
    return this.type === ContentSourceType.PLANNER.key;
  }

  get isAdvisorQuestionnaireType(): boolean {
    return this.type === ContentSourceType.ADVISOR_QUESTIONNAIRE.key;
  }

  get usesPdfDocument(): boolean {
    return this.isPdfType || this.isPlannerType;
  }

  get usesInlineContent(): boolean {
    return this.isTextType || this.isAdvisorQuestionnaireType;
  }

  get hasRequiredInputs(): boolean {
    if (this.isAdvisorQuestionnaireType) {
      return !!this.title && !!this.content && !!this.sourceLastUpdatedAt && !!this.estateType;
    }

    return !!this.type && !!this.title && !!this.estateType;
  }

  get statusText(): string {
    if (!this.status) {
      return '';
    }
    return ContentSourceStatus.getByKey(this.status)?.value ?? '';
  }

  hasChangedTextContent(newSource: ContentSource): boolean {
    return (
      newSource.usesInlineContent &&
      !!newSource.content &&
      markdownToHtml(this.content ?? '') !== markdownToHtml(newSource.content ?? '')
    );
  }

  hasRolesChanged(newSource: ContentSource): boolean {
    const currentRoles: string[] = (this.contentSourceRoles ?? [])
      .flatMap((sourceRole: ContentSourceRole) => (sourceRole.role ? [sourceRole.role] : []))
      .sort();
    const newRoles: string[] = (newSource.contentSourceRoles ?? [])
      .flatMap((sourceRole: ContentSourceRole) => (sourceRole.role ? [sourceRole.role] : []))
      .sort();

    return JSON.stringify(currentRoles) !== JSON.stringify(newRoles);
  }

  hasPlanTypesChanged(newSource: ContentSource): boolean {
    const currentPlanTypes: string[] = [...(this.planTypes ?? [])].sort();
    const newPlanTypes: string[] = [...(newSource.planTypes ?? [])].sort();
    return JSON.stringify(currentPlanTypes) !== JSON.stringify(newPlanTypes);
  }

  hasChanged(newSource: ContentSource): boolean {
    return (
      this.title !== newSource.title ||
      this.type !== newSource.type ||
      this.url !== newSource.url ||
      this.sourceLastUpdatedAt !== newSource.sourceLastUpdatedAt ||
      this.estateType !== newSource.estateType ||
      this.countryId !== newSource.countryId ||
      this.countyId !== newSource.countyId ||
      this.hasPlanTypesChanged(newSource) ||
      this.hasRolesChanged(newSource) ||
      this.hasChangedTextContent(newSource)
    );
  }
}
